import React from 'react';
import { Box, Button, IconButton, Stack, SvgIcon, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { palette, spaces, borderRadius, boxShadows } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../utils/getTextTruncationSx';
import { PodcastDetailsData } from '../../../state/AIAssistantEditorProvider';
import usePodcastAudioDownload from '../../AIAssistant/shared/TextEditor/usePodcastAudioDownload';

export const EXTENDED_PODCAST_CARD_TITLE_TEST_ID = 'extended-podcast-card-title';

// The play/pause SVGs carry their own color, so disabled is conveyed via opacity.
const playButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 44,
  height: 44,
  padding: 0,
  '&.Mui-disabled': {
    opacity: 0.4,
  },
};

const tertiaryButtonStyles = {
  color: palette.secondary.main,
  fontWeight: 500,
  flexShrink: 0,
};

interface ExtendedPodcastArtifactCardProps {
  podcast: PodcastDetailsData;
  onShowDetails: () => void;
  onPlay: () => void;
  testId?: string;
}

// Expanded podcast chat card; the visual shell mirrors ExtendedChatMessageArtifactCard.
const ExtendedPodcastArtifactCard: React.FC<ExtendedPodcastArtifactCardProps> = ({
  podcast,
  onShowDetails,
  onPlay,
  testId = 'extended-podcast-card',
}) => {
  const { t } = useTranslation();
  const { handleAudioDownload, isAudioDownloadDisabled } = usePodcastAudioDownload();

  const isPlayDisabled = !podcast.audioUrl || Boolean(podcast.audioGenerationFailed);

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: palette.background.paperElevation2,
        border: `1px solid ${palette.elevation.outlined}`,
        borderRadius: borderRadius.md,
        overflow: 'hidden',
        boxShadow: boxShadows.elevation1,
        maxHeight: '600px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spaces.sm,
          padding: `${spaces.md} ${spaces.lg} ${spaces.xs} ${spaces.lg}`,
          borderBottom: `1px solid ${palette.divider}`,
        }}
      >
        <Stack direction='row' alignItems='center' gap={spaces.sm} flexGrow={1} minWidth={0}>
          <IconButton
            data-testid='extended-podcast-card-play-button'
            onClick={onPlay}
            disabled={isPlayDisabled}
            aria-label={t('Play')}
            sx={playButtonStyles}
          >
            <SvgIcon
              component={getMuiIcon('playCircle')}
              sx={{ fontSize: '35px' }}
              aria-hidden='true'
            />
          </IconButton>
          <Typography
            data-testid={EXTENDED_PODCAST_CARD_TITLE_TEST_ID}
            variant='subtitle1'
            sx={{
              fontWeight: 500,
              color: palette.text.primary,
              ...getTextTruncationSx(1),
            }}
          >
            {podcast.title || t('Podcast')}
          </Typography>
        </Stack>
        <Stack direction='row' spacing={spaces.xs}>
          <Button
            data-testid='extended-podcast-card-details-button'
            size='medium'
            variant='text'
            color='secondary'
            onClick={onShowDetails}
            startIcon={<SvgIcon component={getMuiIcon('eyeOpen')} />}
            sx={tertiaryButtonStyles}
          >
            {t('Show details')}
          </Button>
          <Button
            data-testid='extended-podcast-card-download-button'
            size='medium'
            variant='text'
            color='secondary'
            onClick={handleAudioDownload}
            disabled={isAudioDownloadDisabled}
            startIcon={<SvgIcon component={getMuiIcon('download')} />}
            sx={tertiaryButtonStyles}
          >
            {t('Download')}
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          padding: `${spaces.xs} ${spaces.lg} ${spaces.lg} ${spaces.lg}`,
          overflowY: 'auto',
          flexGrow: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(palette.text.primary, 0.1),
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: alpha(palette.text.primary, 0.2),
          },
        }}
      >
        <Typography
          data-testid='extended-podcast-card-transcript'
          variant='body2'
          component='div'
          sx={{ color: palette.text.primary, whiteSpace: 'pre-wrap' }}
        >
          {podcast.transcript}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExtendedPodcastArtifactCard;
